import { HttpClient, HttpContext } from '@angular/common/http';
import { Injectable, inject } from '@angular/core';
import { Observable, map } from 'rxjs';

import { SKIP_GBE_ERROR_NOTIFICATION } from '@core/interceptors/gbe-error.interceptor';
import {
  CreateFilterPresetRequest,
  FilterPresetModel,
  FilterPresetScope,
  UpdateFilterPresetRequest
} from '@core/models/filter-preset.model';
import { GbeEndPointV4 } from '@core/models/gbe/gbe-endpoint.enum';
import { GBE_BASE_URL } from '@core/tokens/gbe-url.token';
import { isNil } from '@core/utils/shared-utils';

@Injectable({
  providedIn: 'root'
})
export class FilterPresetsService {
  private readonly gbeUrl = inject(GBE_BASE_URL);
  private readonly http = inject(HttpClient);

  private buildUrl(path: string): string {
    return `${this.gbeUrl}/${path}`;
  }

  /**
   * Writes render their own error inline, so they opt out of the interceptor's toast.
   */
  private inlineErrors(): HttpContext {
    return new HttpContext().set(SKIP_GBE_ERROR_NOTIFICATION, true);
  }

  /**
   * Lists the caller's own presets for `entityType` together with the presets shared on
   * `dataSpaceId`, ordered default-first then by name. Without `dataSpaceId` only the
   * caller's own presets come back — this endpoint does not fall back to the user's
   * default dataspace, so pass the dataspace in view.
   *
   * With `context`, the entity-wide presets come back alongside those bound to that field.
   * Without it, only the entity-wide ones do — which is what keeps an entity list free of
   * presets saved from a reference picker.
   *
   * Query params are written in snake_case by hand: `apiCaseRequestInterceptor` converts
   * request bodies only, so a camelCase param would be dropped by the API and silently
   * return an unfiltered list.
   */
  list$(
    entityType: string,
    dataSpaceId?: number,
    context?: string
  ): Observable<FilterPresetModel[]> {
    const params: Record<string, string> = {
      entity_type: entityType.toUpperCase()
    };
    if (!isNil(dataSpaceId)) {
      params['data_space_id'] = dataSpaceId.toString();
    }
    if (!isNil(context)) {
      params['context'] = context;
    }

    return this.http
      .get<{
        presets: FilterPresetModel[];
      }>(this.buildUrl(GbeEndPointV4.FILTER_PRESETS), { params })
      .pipe(map((response) => response.presets));
  }

  create$(data: CreateFilterPresetRequest): Observable<FilterPresetModel> {
    return this.http
      .post<{ preset: FilterPresetModel }>(
        this.buildUrl(GbeEndPointV4.FILTER_PRESETS),
        {
          name: data.name,
          entityType: data.entityType.toUpperCase(),
          scope: data.scope,
          ...(data.scope === FilterPresetScope.DATASPACE
            ? { dataSpaceId: data.dataSpaceId }
            : {}),
          ...(isNil(data.context) ? {} : { context: data.context }),
          ...(isNil(data.filters) ? {} : { filters: data.filters }),
          ...(isNil(data.search) ? {} : { search: data.search }),
          ...(isNil(data.sort) ? {} : { sort: data.sort }),
          ...(isNil(data.pathFilter) ? {} : { pathFilter: data.pathFilter }),
          ...(isNil(data.isDefault) ? {} : { isDefault: data.isDefault }),
          ...(data.template ? { template: true, parameters: data.parameters ?? [] } : {})
        },
        { context: this.inlineErrors() }
      )
      .pipe(map((response) => response.preset));
  }

  /**
   * The body is rebuilt key by key rather than spread: `scope`, `entityType` and
   * `dataSpaceId` are immutable server-side, so letting a caller's wider object through
   * would turn an innocuous rename into `invalid_operation`. `filters`, `search`, `sort`
   * and `pathFilter` keep an explicit `null` — that is how the API is told to clear them.
   */
  update$(id: number, data: UpdateFilterPresetRequest): Observable<FilterPresetModel> {
    const body: UpdateFilterPresetRequest = {};
    if ('name' in data) {
      body.name = data.name;
    }
    if ('filters' in data) {
      body.filters = data.filters;
    }
    if ('search' in data) {
      body.search = data.search;
    }
    if ('sort' in data) {
      body.sort = data.sort;
    }
    if ('pathFilter' in data) {
      body.pathFilter = data.pathFilter;
    }
    if ('isDefault' in data) {
      body.isDefault = data.isDefault;
    }

    return this.http
      .patch<{
        preset: FilterPresetModel;
      }>(this.buildUrl(`${GbeEndPointV4.FILTER_PRESETS}/${id}`), body, {
        context: this.inlineErrors()
      })
      .pipe(map((response) => response.preset));
  }

  delete$(id: number): Observable<void> {
    return this.http.delete<void>(this.buildUrl(`${GbeEndPointV4.FILTER_PRESETS}/${id}`), {
      context: this.inlineErrors()
    });
  }
}
